import { useApolloClient } from "@apollo/client";
import { Text, useMantineTheme, Title, MantineTheme } from "@mantine/core";
import {
  AlterAgreementRecord,
  GetAlterAgreementRecordPageQuery,
  GetAlterAgreementRecordPageQueryVariables,
  AlterationAgreementType,
  AlterationAgreementStatus,
} from "src/generated/graphql";
import { FetchParams, QueryResponse } from "@kapex/tables";
import { ArrayElement } from "src/utils/helperTypes";
import { useCallback, useContext, useEffect, useState } from "react";
import { AppContext } from "src/api/AppContext";
import { DEFAULT_ORDER, ROUTE_NAMES } from "src/utils/constants";
import { useModals } from "@mantine/modals";
import { GetAlterAgreementRecordPage } from "src/api/graphql/queries/AlterAgreementRecord";
import ScrollableDataGrid from "src/components/ScrollableDataGrid";
import RequestDetails from "./RequestDetails";

export type AlterAgreementRequests = ArrayElement<
  GetAlterAgreementRecordPageQuery["alterAgreementRecordPage"]["items"]
>;

const Requests = () => {
  const modals = useModals();
  const { setCurrentPageTitle, currentLocation } = useContext(AppContext);
  const theme = useMantineTheme();
  const { query } = useApolloClient();
  const [refetchTableData, setrefetchTableData] = useState(false);

  const getRequests = async (
    fetchParams: FetchParams
  ): Promise<QueryResponse<AlterAgreementRequests>> => {
    let { data } = await query<
      GetAlterAgreementRecordPageQuery,
      GetAlterAgreementRecordPageQueryVariables
    >({
      query: GetAlterAgreementRecordPage,
      variables: {
        pagination: {
          count: fetchParams.count,
          page: fetchParams.page,
          orderBy: fetchParams.orderBy || DEFAULT_ORDER.name,
          filters: fetchParams.filters,
        },
        locationId: currentLocation!.id,
      },
    });

    if (data.alterAgreementRecordPage) {
      return {
        rowCount: data.alterAgreementRecordPage.totalRows,
        rowData: data.alterAgreementRecordPage
          .items as AlterAgreementRequests[],
      };
    }
    setrefetchTableData(false);
    return {
      rowData: [],
      rowCount: 0,
    };
  };

  useEffect(() => {
    setCurrentPageTitle(ROUTE_NAMES.REQUESTS);
    return () => {
      setCurrentPageTitle(undefined);
    };
  }, []);

  const handleModal = (rowItem: AlterAgreementRequests) => {
    modals.openModal({
      closeOnClickOutside: false,
      title: "Request Details",
      children: (
        <RequestDetails
          item={rowItem}
          closeModal={() => modals.closeAll()}
          onRefetch={() => setrefetchTableData(!refetchTableData)}
        />
      ),
    });
  };

  const ShowTable = useCallback(
    ({ theme }: { theme: MantineTheme }) => {
      return (
        <div>
          <ScrollableDataGrid<AlterAgreementRequests>
            columnConfigs={[
              {
                field: "name",
                name: "Client",
                filter: {
                  field: "name",
                  type: "text",
                  parentRelationField: "signedAgreement.client.user",
                },
                sortable: true,
                cellRenderer: (rowItem) => (
                  <Text
                    weight={500}
                    onClick={() => handleModal(rowItem)}
                    style={{ cursor: "pointer" }}
                  >
                    {`${rowItem.signedAgreement.client.user.firstName} ${rowItem.signedAgreement.client.user.lastName}`}
                  </Text>
                ),
              },
              {
                field: "alterationAgreementType",
                name: "Type",
                filter: {
                  field: "alterationAgreementType",
                  type: "select",
                  options: (
                    Object.keys(AlterationAgreementType) as Array<
                      keyof typeof AlterationAgreementType
                    >
                  ).map((key) => key),
                },
                sortable: true,
                cellRenderer: (rowItem) => (
                  <Text weight={500}>{rowItem.alterationAgreementType}</Text>
                ),
              },
              {
                field: "alterationAgreementStatus",
                name: "Status",
                filter: {
                  field: "alterationAgreementStatus",
                  type: "select",
                  options: (
                    Object.keys(AlterationAgreementStatus) as Array<
                      keyof typeof AlterationAgreementStatus
                    >
                  ).map((key) => key),
                },
                sortable: true,
                cellRenderer: (rowItem) => (
                  <Text weight={500}>{rowItem.alterationAgreementStatus}</Text>
                ),
              },
              {
                field: "alterAgreementRecord_createdDate",
                name: "Submitted",
                filter: {
                  field: "createdDate",
                  type: "dateRange",
                },
                sortable: true,
                valueParser: (rowItem) => {
                  return rowItem.createdDate
                    ? `${new Date(
                        rowItem.createdDate
                      ).toDateString()}, ${new Date(
                        rowItem.createdDate
                      ).toLocaleTimeString()}`
                    : "N/A";
                },
              },
              {
                field: "reviewedAt",
                name: "Reviewed",
                filter: {
                  field: "reviewedAt",
                  type: "dateRange",
                },
                sortable: true,
                valueParser: (rowItem) => {
                  return rowItem.reviewedAt
                    ? `${new Date(
                        rowItem.reviewedAt
                      ).toDateString()}, ${new Date(
                        rowItem.reviewedAt
                      ).toLocaleTimeString()}`
                    : "N/A";
                },
              },
            ]}
            dataFetch={getRequests}
          />
        </div>
      );
    },
    [currentLocation, refetchTableData]
  );

  if (!currentLocation?.id)
    return <Title order={3}>Please Select Location from Header</Title>;
  return (
    <>
      <ShowTable theme={theme} />
    </>
  );
};

export default Requests;
